/* ============================================================
   Flow Wake-Up Quiz — Screens B
   Exposes: CalibrationScreen, EmailGateScreen, RevealScreen
   ============================================================ */

(function () {
  var jsx = window.jsx;
  var useState = React.useState;
  var useEffect = React.useEffect;
  var useRef = React.useRef;

  function haptic(method, options) {
    var h = window.FlowHaptics;
    if (h && typeof h[method] === 'function') h[method](options);
  }

  // ------------------------------------------------------------
  // CalibrationScreen — "matching you with your plan"
  // ------------------------------------------------------------
  function CalibrationScreen(props) {
    // Demo-aware "comparing against…" line. Falls back to a generic
    // "50,000+ Flow users" if the user hasn't filled demos (paranoia —
    // they should always be filled by this point in the sequence).
    var demo = window.demoDisplay ? window.demoDisplay(props.answers || {}) : { hasDemo: false };
    var compareLine = demo.hasDemo
      ? 'Comparing against thousands of ' + demo.gender + ' aged ' + demo.ageBucket
      : 'Comparing against 50,000+ Flow users';

    return jsx('div', {
      className: 'screen',
      children: jsx(window.CalibrationLoader, {
        title: 'Matching you with your Morning Archetype…',
        perLineMs: 900,
        lines: [
          'Scoring your wake-up pattern',
          compareLine,
          'Identifying your archetype',
          'Building your personalized plan'
        ],
        onComplete: props.onComplete
      })
    });
  }
  window.CalibrationScreen = CalibrationScreen;

  // ------------------------------------------------------------
  // EmailGateScreen — gate placement is the single biggest
  // conversion lever. Show this right before the reveal, when
  // tension is highest.
  // ------------------------------------------------------------
  function EmailGateScreen(props) {
    var emailRef = useRef(null);
    var stateRef = useState('');
    var email = stateRef[0];
    var setEmail = stateRef[1];
    var errorRef = useState('');
    var error = errorRef[0];
    var setError = errorRef[1];
    var loadingRef = useState(false);
    var loading = loadingRef[0];
    var setLoading = loadingRef[1];

    useEffect(function () {
      if (emailRef.current) emailRef.current.focus({ preventScroll: true });
    }, []);

    function isValidEmail(v) {
      return typeof v === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());
    }

    function submit(e) {
      if (e && e.preventDefault) e.preventDefault();
      var clean = (email || '').trim();
      if (!isValidEmail(clean)) {
        haptic('error', { key: 'email-invalid' });
        setError("That doesn't look like a valid email.");
        return;
      }
      setError('');
      setLoading(true);
      haptic('success', { key: 'email-submit' });
      submitToShopify(clean, props.answers, props.winner);
      window.trackEvent('email_gate_submitted', { winner: props.winner });
      // Move forward immediately — don't wait for Shopify (it's a fire-and-forget
      // via hidden iframe). If Shopify is down, the email is also persisted to
      // localStorage so a server-side reconciler could pick it up.
      try { localStorage.setItem('flow-quiz-' + (window.__QUIZ_FUNNEL || 'wakeup') + '-email', clean); } catch (e2) {}
      setTimeout(function () { props.onSubmit(clean); }, 280);
    }

    function submitToShopify(emailAddr, answers, winner) {
      var form = document.getElementById('quiz-email-form');
      if (!form) return;
      var emailInput = form.querySelector('[data-quiz-email-value]');
      var tagInput   = form.querySelector('[data-quiz-email-tags]');
      var noteInput  = form.querySelector('[data-quiz-email-note]');
      var demo = window.demoDisplay ? window.demoDisplay(answers || {}) : { ageBucket: '', gender: '' };
      // Tags are searchable as Klaviyo segments. Tag granularity: archetype,
      // age bucket, gender — so flows can target e.g. "Snoozer + 25-34 + male".
      var tags = ['quiz-wakeup', 'archetype-' + winner];
      if (demo.ageBucket) tags.push('age-' + demo.ageBucket.replace(/\s+/g, '').replace('–', '-'));
      if (demo.gender)    tags.push('gender-' + demo.gender);
      if (emailInput) emailInput.value = emailAddr;
      if (tagInput)   tagInput.value   = tags.join(', ');
      if (noteInput)  noteInput.value  = JSON.stringify({ funnel: 'wakeup', winner: winner, age: demo.ageBucket, gender: demo.gender, answers: answers || {} });
      form.target = 'flow-quiz-email-frame';
      try { form.submit(); } catch (e) { /* swallow */ }
    }

    function skip() {
      haptic('advance', { key: 'email-skip' });
      window.trackEvent('email_gate_skipped', { winner: props.winner });
      if (props.onSkip) props.onSkip(); else props.onSubmit('');
    }

    // Figma final (11888:8184) — title + subtitle, single email input, a
    // "Skip" text link, and a white "Claim Reward" button pinned to the
    // bottom. (The reward perks now live on the reveal grid, not here.)
    return jsx('div', {
      className: 'screen gate',
      children: [
        jsx('div', {
          className: 'gate__head',
          children: [
            jsx('h1', {
              className: 'gate__title',
              children: 'Drop your email to claim your reward!'
            }),
            jsx('p', {
              className: 'gate__sub',
              children: "We'll send a code to your email address — use it to activate Flow+ on your Flow Alarm app!"
            })
          ]
        }),

        jsx('form', {
          id: 'quiz-email-entry',
          onSubmit: submit,
          className: 'gate__form',
          noValidate: true,
          children: [
            jsx('input', {
              id: 'quiz-email',
              ref: emailRef,
              type: 'email',
              autoComplete: 'email',
              inputMode: 'email',
              className: 'gate__input',
              placeholder: 'Your email...',
              value: email,
              onChange: function (e) { setEmail(e.target.value); setError(''); }
            }),
            error ? jsx('p', { className: 'gate__error', children: error }) : null,

            jsx('button', {
              type: 'button',
              className: 'btn btn--text gate__skip',
              onClick: skip,
              children: 'Skip'
            })
          ]
        }),

        jsx('div', {
          className: 'cta-row cta-row--sticky',
          children: jsx('button', {
            type: 'submit',
            form: 'quiz-email-entry',
            className: 'btn btn--primary btn--light gate__cta',
            disabled: loading,
            children: loading ? 'Sending...' : 'Claim Reward'
          })
        })
      ]
    });
  }
  window.EmailGateScreen = EmailGateScreen;

  // ------------------------------------------------------------
  // ScratchCardScreen — canvas-based scratch interaction that reveals
  // the user's archetype underneath. Replaces the simpler tap-to-reveal
  // "Unlock" card. The mechanic:
  //
  //   1. Canvas covers a hidden archetype preview (name + glow)
  //   2. Pointer drag (mouse or touch) erases pixels via
  //      `globalCompositeOperation = 'destination-out'`
  //   3. Image data is sampled every ~12 strokes to estimate erased %
  //   4. At ≥40% erased, the rest auto-clears with a fade animation
  //   5. After the fade, `onScratched()` advances to the full reveal
  //
  // prefers-reduced-motion: the scratch UI is skipped — the user sees
  // a direct "Reveal my archetype" button instead.
  // ------------------------------------------------------------
  function ScratchCardScreen(props) {
    var archetype = window.ARCHETYPES[props.winner];
    var canvasRef = useRef(null);
    var stateRef = useState(false);
    var revealed = stateRef[0];
    var setRevealed = stateRef[1];
    var progRef = useState(0);
    var prog = progRef[0];
    var setProg = progRef[1];

    var reduceMotion = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // ---- Canvas init ----
    // Re-tries on requestAnimationFrame until the canvas has real
    // dimensions. The cross-fade transition can keep the screen at
    // measured 0×0 for a frame or two — without the retry, the canvas
    // never paints and the user sees right through to the archetype name
    // underneath. ResizeObserver also catches DPR / orientation changes.
    useEffect(function () {
      if (reduceMotion) return;
      var raf = 0, ro;
      function paintOnce() {
        var canvas = canvasRef.current;
        if (!canvas) return false;
        var rect = canvas.getBoundingClientRect();
        if (rect.width < 4 || rect.height < 4) return false;
        var dpr = window.devicePixelRatio || 1;
        canvas.width  = Math.round(rect.width * dpr);
        canvas.height = Math.round(rect.height * dpr);
        var ctx = canvas.getContext('2d');
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.scale(dpr, dpr);
        var grad = ctx.createLinearGradient(0, 0, rect.width, rect.height);
        grad.addColorStop(0, '#ec6324');
        grad.addColorStop(0.55, '#d45920');
        grad.addColorStop(1, '#bd4f1d');
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, rect.width, rect.height);
        ctx.fillStyle = 'rgba(0,0,0,0.08)';
        for (var i = 0; i < rect.width + rect.height; i += 22) {
          ctx.fillRect(i, 0, 2, rect.height);
        }
        ctx.fillStyle = 'rgba(255,255,255,0.92)';
        ctx.font = '700 16px "Inter", "Inter Display", sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('SCRATCH', rect.width / 2, rect.height / 2 - 12);
        ctx.font = '700 13px "Inter", sans-serif';
        ctx.fillText('to reveal', rect.width / 2, rect.height / 2 + 12);
        return true;
      }
      function retry() {
        if (!paintOnce()) raf = requestAnimationFrame(retry);
      }
      retry();
      // Repaint if the container resizes (orientation change, etc).
      if (typeof ResizeObserver !== 'undefined' && canvasRef.current) {
        ro = new ResizeObserver(function () { paintOnce(); });
        ro.observe(canvasRef.current);
      }
      return function () {
        cancelAnimationFrame(raf);
        if (ro) ro.disconnect();
      };
    }, [reduceMotion]);

    // ---- Pointer handlers ----
    var drawingRef = useRef(false);
    var lastRef = useRef(null);
    var strokeCountRef = useRef(0);

    function getPos(e, canvas) {
      var rect = canvas.getBoundingClientRect();
      var x, y;
      if (e.touches && e.touches[0]) { x = e.touches[0].clientX; y = e.touches[0].clientY; }
      else { x = e.clientX; y = e.clientY; }
      return { x: x - rect.left, y: y - rect.top };
    }

    function scratchAt(pt) {
      var canvas = canvasRef.current;
      if (!canvas) return;
      var ctx = canvas.getContext('2d');
      ctx.globalCompositeOperation = 'destination-out';
      // Stroke from last point so quick drags don't leave gaps.
      var last = lastRef.current;
      if (last) {
        ctx.beginPath();
        ctx.lineCap = 'round';
        ctx.lineWidth = 36;
        ctx.moveTo(last.x, last.y);
        ctx.lineTo(pt.x, pt.y);
        ctx.stroke();
      }
      ctx.beginPath();
      ctx.arc(pt.x, pt.y, 22, 0, Math.PI * 2);
      ctx.fill();
      lastRef.current = pt;
      ctx.globalCompositeOperation = 'source-over';
    }

    function sampleProgress() {
      var canvas = canvasRef.current;
      if (!canvas) return 0;
      var ctx = canvas.getContext('2d');
      // Subsample: read a 32x32 grid of pixels instead of every pixel.
      var w = canvas.width, h = canvas.height;
      var stepX = Math.max(1, Math.floor(w / 32));
      var stepY = Math.max(1, Math.floor(h / 32));
      var transparent = 0, total = 0;
      // getImageData is expensive — keep sample count tiny.
      var img = ctx.getImageData(0, 0, w, h);
      var data = img.data;
      for (var y = 0; y < h; y += stepY) {
        for (var x = 0; x < w; x += stepX) {
          var idx = (y * w + x) * 4 + 3;
          total++;
          if (data[idx] < 32) transparent++;
        }
      }
      return total === 0 ? 0 : transparent / total;
    }

    function handleDown(e) {
      haptic('selection', { key: 'scratch-start', minInterval: 120 });
      drawingRef.current = true;
      lastRef.current = null;
      // Pointer capture keeps move/up events firing on the canvas even
      // when the cursor exits the canvas bounds. Without this on macOS,
      // a fast drag that leaves the card mid-stroke would freeze.
      try {
        if (e.pointerId !== undefined && e.currentTarget && e.currentTarget.setPointerCapture) {
          e.currentTarget.setPointerCapture(e.pointerId);
        }
      } catch (err) { /* swallow — older browsers */ }
      // Only preventDefault on touch — calling it on mouse pointerdown
      // breaks the mouse drag on macOS Chrome/Safari.
      if (e.pointerType === 'touch') e.preventDefault();
      scratchAt(getPos(e, canvasRef.current));
    }
    function handleMove(e) {
      if (!drawingRef.current) return;
      if (e.pointerType === 'touch') e.preventDefault();
      scratchAt(getPos(e, canvasRef.current));
      strokeCountRef.current++;
      haptic('selection', { key: 'scratch-move', minInterval: 130 });
      if (strokeCountRef.current % 8 === 0) {
        var p = sampleProgress();
        setProg(p);
        // At 40% scratched, fade out the canvas to reveal the full book.
        // We DO NOT auto-advance — the user should see the reward and
        // tap "Claim Reward" themselves (matches Figma's beat).
        if (p >= 0.4 && !revealed) {
          haptic('success', { key: 'scratch-reveal' });
          setRevealed(true);
        }
      }
    }
    function handleUp(e) {
      drawingRef.current = false;
      lastRef.current = null;
      try {
        if (e && e.pointerId !== undefined && e.currentTarget && e.currentTarget.releasePointerCapture) {
          e.currentTarget.releasePointerCapture(e.pointerId);
        }
      } catch (err) {}
    }

    // Reveal without auto-advance — the user clicks "Claim Reward" below
    // when they're ready.
    function manualReveal() {
      if (revealed) return;
      haptic('success', { key: 'scratch-manual-reveal' });
      setRevealed(true);
    }

    function claimReward() {
      haptic('advance', { key: 'scratch-claim' });
      props.onScratched && props.onScratched();
    }

    var coverClass = 'scratch__cover' + (revealed ? ' is-cleared' : '');

    var QA = window.QA || {};
    var bookImg = QA['digital-detox-guide.png'] || '../assets/digital-detox-guide.png';

    return jsx('div', {
      className: 'screen scratch-screen',
      children: [
        jsx('h2', {
          className: 'scratch__lead',
          children: revealed
            ? ['You earned ', jsx('span', { className: 'scratch__lead-accent', children: 'Digital Detox Guide' }), ' by Flow!']
            : "Making it this far is proof that you're taking the first step."
        }),
        jsx('p', {
          className: 'scratch__hint scratch__hint--top',
          children: revealed
            ? null
            : [jsx('span', { 'aria-hidden': true, children: '👉 ' }), 'Scratch to reveal your reward']
        }),

        jsx('div', {
          className: 'scratch__card',
          children: [
            // Underneath: the Digital Detox Guide book image revealed by scratching.
            jsx('div', {
              className: 'scratch__under',
              children: [
                jsx('div', { className: 'scratch__under-glow', 'aria-hidden': true }),
                jsx('img', {
                  src: bookImg,
                  alt: 'Digital Detox Guide',
                  className: 'scratch__under-book'
                })
              ]
            }),
            // The scratch surface — canvas (default) or button (reduced motion / fallback).
            reduceMotion
              ? jsx('button', {
                  type: 'button',
                  className: coverClass + ' scratch__cover--button',
                  onClick: manualReveal,
                  children: 'Tap to reveal'
                })
              : jsx('canvas', {
                  ref: canvasRef,
                  className: coverClass,
                  // Pointer events handle BOTH mouse + touch.
                  // Pointer capture is set in handleDown so pointermove
                  // continues to fire even if the cursor leaves the canvas.
                  // pointerleave intentionally not handled — capture
                  // prevents drags from being abandoned when the pointer
                  // briefly exits the canvas bounds.
                  onPointerDown: handleDown,
                  onPointerMove: handleMove,
                  onPointerUp:   handleUp,
                  onPointerCancel: handleUp
                })
          ]
        }),

        // Below the card: either a "scratching…" hint + accessibility
        // fallback (pre-reveal), or the orange "Claim Reward" CTA
        // (post-reveal) that advances to the Dock showcase.
        revealed
          ? jsx('div', {
              className: 'cta-row cta-row--sticky',
              children: jsx('button', {
                type: 'button',
                className: 'btn btn--primary',
                onClick: claimReward,
                children: 'Claim Reward'
              })
            })
          : jsx('div', {
              className: 'scratch__below',
              children: [
                jsx('p', {
                  className: 'scratch__progress',
                  children: prog > 0.05 ? 'Keep going…' : ''
                }),
                jsx('button', {
                  type: 'button',
                  className: 'btn btn--text scratch__skip',
                  onClick: manualReveal,
                  children: 'Reveal it for me'
                })
              ]
            })
      ]
    });
  }
  window.ScratchCardScreen = ScratchCardScreen;

  // ------------------------------------------------------------
  // DockShowcaseScreen — Figma's "Now meet the Flow Dock" beat.
  // Sits between the archetype reveal and the LP redirect. Shows
  // the Dock product photo with a "this is what makes it work" framing.
  // ------------------------------------------------------------
  function DockShowcaseScreen(props) {
    var QA = window.QA || {};
    // Real Flow Dock image extracted from the Figma showcase frame (node
    // 11821:15120). Falls back to the previous Shopify storefront asset
    // only if the Figma export isn't available.
    var dockImg = QA['flow-dock-figma.png']
      || QA['dock-product-photo.png']
      || '../assets/flow-dock-figma.png';

    useEffect(function () {
      window.trackEvent('dock_showcase_view', { recommended_result: props.winner });
    }, [props.winner]);

    // Auto-advance to the offer PDP after 7s if the user doesn't tap the CTA.
    // Navigating (tap or timer) unmounts this screen, which clears the timer.
    useEffect(function () {
      var t = setTimeout(function () {
        window.trackEvent('dock_showcase_autoadvance', { recommended_result: props.winner });
        props.onContinue && props.onContinue();
      }, 7000);
      return function () { clearTimeout(t); };
    }, []);

    return jsx('div', {
      className: 'screen dock-showcase',
      children: [
        jsx('div', {
          className: 'dock-showcase__head',
          children: [
            // Figma's exact copy — "reward is locked in" (matches the
            // Digital Detox Guide claim, not subscription-style language).
            jsx('h1', { className: 'dock-showcase__title', children: 'Your reward is locked in!' }),
            jsx('p', {
              className: 'dock-showcase__sub',
              children: [
                'Now meet the Flow Dock that ',
                jsx('span', { className: 'dock-showcase__accent', children: 'makes it work.' })
              ]
            })
          ]
        }),

        jsx('div', {
          className: 'dock-showcase__product',
          children: [
            jsx('div', { className: 'dock-showcase__halo', 'aria-hidden': true }),
            jsx('img', {
              src: dockImg,
              alt: 'Flow Dock',
              className: 'dock-showcase__photo',
              width: 320,
              height: 320,
              loading: 'eager'
            })
          ]
        }),

        jsx('div', {
          className: 'cta-row cta-row--sticky',
          children: jsx('button', {
            className: 'btn btn--primary',
            onClick: function () {
              haptic('advance', { key: 'dock-showcase-continue' });
              props.onContinue();
            },
            children: 'See Flow Dock →'
          })
        })
      ]
    });
  }
  window.DockShowcaseScreen = DockShowcaseScreen;

  // ------------------------------------------------------------
  // UnlockScreen — flip-to-discover reward game (Figma 11821:15049 /
  // 11821:15135). A 3×3 grid of glowing orange "?" mystery cards. The
  // user taps cards one at a time; each does a 3D flip and reveals the
  // next of the four real gifts (Free Shipping, Flow+ Trial, iOS
  // Companion App, Digital Detox Guide) in a shuffled order, so every
  // play deals them into different cells. After all four are found the
  // remaining cards dim and an orange "Claim Rewards" button advances.
  // ------------------------------------------------------------
  var UNLOCK_GIFTS = [
    { img: 'perk-free-shipping.png', label: 'Free Shipping' },
    { img: 'perk-flow-plus.png',     label: 'Flow+ Trial' },
    { img: 'perk-ios-app.png',       label: 'iOS Companion App' },
    { img: 'perk-digital-detox.png', label: 'Digital Detox Guide' }
  ];

  function UnlockScreen(props) {
    var QA = window.QA || {};

    // Shuffle the gift order once per mount so the reveal order varies.
    var orderRef = useRef(null);
    if (!orderRef.current) {
      var shuffled = UNLOCK_GIFTS.slice();
      for (var s = shuffled.length - 1; s > 0; s--) {
        var r = Math.floor(Math.random() * (s + 1));
        var tmp = shuffled[s]; shuffled[s] = shuffled[r]; shuffled[r] = tmp;
      }
      orderRef.current = shuffled;
    }
    var gifts = orderRef.current;
    var total = gifts.length;

    // revealed maps a cell index -> the gift index dealt to it.
    var revealedRef = useState({});
    var revealed = revealedRef[0];
    var setRevealed = revealedRef[1];
    var count = Object.keys(revealed).length;
    var done = count >= total;

    function flip(c) {
      if (done || revealed[c] !== undefined) return;
      var next = Object.assign({}, revealed);
      next[c] = count; // deal the next gift in shuffled order
      haptic('advance', { key: 'unlock-flip' });
      setRevealed(next);
      window.trackEvent('unlock_card_flipped', { n: count + 1 });
      if (count + 1 >= total) haptic('success', { key: 'unlock-complete' });
    }
    function claim() {
      haptic('success', { key: 'unlock-claim' });
      window.trackEvent('unlock_claimed');
      props.onUnlock && props.onUnlock();
    }

    var cells = [0,1,2,3,4,5,6,7,8].map(function (c) {
      var giftIdx = revealed[c];
      var isFlipped = giftIdx !== undefined;
      var gift = isFlipped ? gifts[giftIdx] : null;
      var dimmed = done && !isFlipped;
      var cls = 'unlock__cell'
        + (isFlipped ? ' is-flipped' : '')
        + (dimmed ? ' is-dimmed' : '');
      return jsx('button', {
        key: 'c' + c,
        type: 'button',
        className: cls,
        disabled: isFlipped || done,
        'aria-label': isFlipped ? gift.label : 'Flip card to reveal a reward',
        onClick: function () { flip(c); },
        children: jsx('div', {
          className: 'unlock__flip',
          children: [
            jsx('div', {
              className: 'unlock__face unlock__face--front',
              children: jsx('span', { className: 'unlock__q', 'aria-hidden': true, children: '?' })
            }),
            jsx('div', {
              className: 'unlock__face unlock__face--back',
              children: gift ? [
                jsx('img', { key: 'i', className: 'unlock__perk-img', src: (QA[gift.img] || ('../assets/' + gift.img)), alt: '' }),
                jsx('span', { key: 'l', className: 'unlock__perk-label', children: gift.label })
              ] : null
            })
          ]
        })
      });
    });

    return jsx('div', {
      className: 'screen unlock',
      children: [
        jsx('h1', {
          className: 'unlock__title',
          children: [
            jsx('span', {
              className: 'unlock__title-line',
              children: "It's time to become the best version of yourself."
            }),
            jsx('span', {
              className: 'unlock__title-line unlock__title-line--cta',
              children: done
                ? "Making it this far is proof that you're taking the first step."
                : [jsx('span', { 'aria-hidden': true, children: '👉 ' }), 'Flip the cards to reveal your rewards (' + count + '/' + total + ')']
            })
          ]
        }),

        jsx('div', { className: 'unlock__grid', children: cells }),

        done
          ? jsx('div', {
              className: 'cta-row cta-row--sticky unlock__cta',
              children: jsx('button', {
                type: 'button',
                className: 'btn btn--primary unlock__claim',
                onClick: claim,
                children: 'Claim Rewards'
              })
            })
          : null
      ]
    });
  }
  window.UnlockScreen = UnlockScreen;

  // ------------------------------------------------------------
  // RevealScreen — the payoff
  // ------------------------------------------------------------
  function RevealScreen(props) {
    var archetype = window.ARCHETYPES[props.winner];
    if (!archetype) return null;

    useEffect(function () {
      // Recolor the body NOW so the reveal screen + LP both share accent.
      document.body.setAttribute('data-result', props.winner);
      window.trackEvent('reveal_shown', { recommended_result: props.winner });
    }, [props.winner]);

    // Build the demo-anchored opening line (Barnum amplifier).
    // Skipped silently if either age or gender is missing.
    var demo = window.demoDisplay ? window.demoDisplay(props.answers || {}) : { hasDemo: false };
    var demoLine = (archetype.revealDemoLine && demo.hasDemo)
      ? window.demoInterpolate(archetype.revealDemoLine, demo)
      : null;

    // Bullets are now structured cards: { icon, title, subtitle, accent? }.
    // The first card with an `accent` property gets the "hero" treatment —
    // tinted background + orange tail text, matching the Figma's first
    // featured row in the "best version" timeline screen.
    var bullets = archetype.revealBullets || [];

    return jsx('div', {
      className: 'screen reveal',
      children: [
        jsx('div', {
          className: 'reveal__head',
          children: [
            jsx('div', { className: 'reveal__kicker', children: archetype.revealKicker || 'Your match' }),
            jsx('h1', { className: 'reveal__name', children: archetype.name }),
            jsx('h2', { className: 'reveal__tagline', children: archetype.tagline }),
            demoLine ? jsx('p', { className: 'reveal__demo-line', children: demoLine }) : null
          ]
        }),

        jsx('ul', {
          className: 'reveal__cards',
          children: bullets.map(function (b, i) {
            // Backwards-compat: if a bullet is still a plain string, render it
            // as a card with a default check icon and no subtitle.
            if (typeof b === 'string') {
              return jsx('li', {
                key: String(i),
                className: 'feat-card',
                children: [
                  jsx('span', { className: 'feat-card__icon', children: jsx(window.CheckIcon, { size: 14 }) }),
                  jsx('span', { className: 'feat-card__body', children: jsx('span', { className: 'feat-card__title', children: b }) })
                ]
              });
            }
            return jsx('li', {
              key: String(i),
              className: 'feat-card' + (b.accent ? ' feat-card--hero' : ''),
              children: [
                jsx('span', { className: 'feat-card__icon', children: b.icon }),
                jsx('span', {
                  className: 'feat-card__body',
                  children: [
                    jsx('span', { className: 'feat-card__title', children: b.title }),
                    b.subtitle ? jsx('span', { className: 'feat-card__sub', children: b.subtitle }) : null
                  ]
                }),
                b.accent ? jsx('span', { className: 'feat-card__accent', children: b.accent }) : null
              ]
            });
          })
        }),

        jsx('div', {
          className: 'cta-row cta-row--sticky',
          children: [
            jsx('button', {
              className: 'btn btn--primary',
              onClick: function () {
                haptic('advance', { key: 'reveal-see-plan' });
                props.onSeePlan();
              },
              children: 'See my full plan →'
            }),
            jsx('button', {
              className: 'btn btn--text',
              onClick: function () {
                haptic('success', { key: 'reveal-share' });
                props.onShare();
              },
              children: 'Share my archetype'
            })
          ]
        })
      ]
    });
  }
  window.RevealScreen = RevealScreen;
})();
